import React, { useState, useEffect } from 'react';
import { X, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { Database } from '../lib/database.types';
import { useTranslation } from '../hooks/useTranslation';

type Car = Database['public']['Tables']['cars']['Row'];

type Profile = {
  first_name: string;
  last_name: string;
  phone: string | null;
  secondary_phone: string | null;
  username: string;
  email: string;
} | null;

interface VehicleDetailsModalProps {
  car: Car;
  onClose: () => void;
  onApprove: (carId: string, details: {
    brand: string;
    model: string;
    year: string;
    chassisNumber: string;
    plateState: string;
    plateCode: string;
    plateNumber: string;
  }) => void;
  onReject: (carId: string, reason: string) => void;
}

export function VehicleDetailsModal({ car, onClose, onApprove, onReject }: VehicleDetailsModalProps) {
  const { t } = useTranslation();
  const [showImages, setShowImages] = useState<{ front: boolean; back: boolean }>({ front: true, back: true });
  const [details, setDetails] = useState({
    brand: car.brand || '',
    model: car.model || '',
    year: car.year ? String(car.year) : '',
    chassisNumber: car.chassis_number || '',
    plateState: car.plate_state || '',
    plateCode: car.plate_code || '',
    plateNumber: car.plate_number || ''
  });
  const [rejectionReason, setRejectionReason] = useState('');
  const [isRejecting, setIsRejecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageLoadError, setImageLoadError] = useState<{ front: boolean; back: boolean }>({ front: false, back: false });
  const [customerProfile, setCustomerProfile] = useState<Profile>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  useEffect(() => {
    let isMounted = true;
    let retryCount = 0;
    const maxRetries = 3;
    const retryDelay = 1000; // 1 second

    const fetchCustomerProfile = async () => {
      if (!car.profile_id) {
        setLoadingProfile(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('first_name, last_name, phone, secondary_phone, username, email')
          .eq('id', car.profile_id)
          .maybeSingle();

        if (error) throw error;
        
        if (isMounted) {
          if (data) {
            setCustomerProfile(data);
            setLoadingProfile(false);
          } else if (retryCount < maxRetries) {
            retryCount++;
            setTimeout(fetchCustomerProfile, retryDelay);
          } else {
            setLoadingProfile(false);
            setError('Could not load customer information');
          }
        }
      } catch (err) {
        console.error('Error fetching customer profile:', err);
        if (isMounted && retryCount < maxRetries) {
          retryCount++;
          setTimeout(fetchCustomerProfile, retryDelay);
        } else if (isMounted) {
          setLoadingProfile(false);
          setError('Failed to load customer information');
        }
      }
    };

    fetchCustomerProfile();

    return () => {
      isMounted = false;
    };
  }, [car.profile_id]);

  const handleApprove = () => {
    // Validate required fields
    if (!details.brand || !details.model || !details.year || !details.chassisNumber) {
      setError('Please fill in all required fields');
      return;
    }

    // Validate year format
    const yearNum = parseInt(details.year);
    if (isNaN(yearNum) || yearNum < 1900 || yearNum > new Date().getFullYear() + 1) {
      setError('Please enter a valid year');
      return;
    }

    // Validate plate information
    if (details.plateState || details.plateCode || details.plateNumber) {
      if (!details.plateState || !details.plateCode || !details.plateNumber) {
        setError('Please fill in all plate information or leave all fields empty');
        return;
      }
    }

    setError(null);
    onApprove(car.id, details);
  };

  const renderImage = (url: string | null, side: 'front' | 'back', alt: string) => {
    if (!url) return null;

    if (imageLoadError[side]) {
      return (
        <div className="flex items-center justify-center h-48 bg-gray-100 dark:bg-gray-700">
          <p className="text-gray-500 dark:text-gray-400">Failed to load image</p>
        </div>
      );
    }

    return (
      <img
        src={url}
        alt={alt}
        className="w-full h-auto max-h-[600px] object-contain rounded-lg shadow-lg"
        onError={() => {
          setImageLoadError(prev => ({ ...prev, [side]: true }));
          console.error(`Failed to load ${side} image:`, url);
        }}
      />
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Review Vehicle Registration
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded flex items-start">
            <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Customer Information */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
            Customer Information
          </h3>
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            {loadingProfile ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
              </div>
            ) : customerProfile ? (
              <div className="space-y-2">
                <p className="text-gray-600 dark:text-gray-300">
                  <span className="font-semibold">Name:</span> {customerProfile.first_name} {customerProfile.last_name}
                </p>
                <p className="text-gray-600 dark:text-gray-300">
                  <span className="font-semibold">Username:</span> @{customerProfile.username}
                </p>
                <p className="text-gray-600 dark:text-gray-300">
                  <span className="font-semibold">Email:</span> {customerProfile.email}
                </p>
                {customerProfile.phone && (
                  <p className="text-gray-600 dark:text-gray-300">
                    <span className="font-semibold">Phone:</span> {customerProfile.phone}
                  </p>
                )}
                {customerProfile.secondary_phone && (
                  <p className="text-gray-600 dark:text-gray-300">
                    <span className="font-semibold">Secondary Phone:</span> {customerProfile.secondary_phone}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-gray-600 dark:text-gray-300">
                {error ? 'Failed to load customer information' : 'No customer information available'}
              </p>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Registration Card Front */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Registration Card (Front)
              </h3>
              <button
                onClick={() => setShowImages(prev => ({ ...prev, front: !prev.front }))}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400"
              >
                {showImages.front ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {showImages.front && (
              <div className="relative bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
                {car.registration_front_url ? (
                  renderImage(car.registration_front_url, 'front', "Registration Card Front")
                ) : (
                  <div className="flex items-center justify-center h-48">
                    <p className="text-gray-500 dark:text-gray-400">No image available</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Registration Card Back */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Registration Card (Back)
              </h3>
              <button
                onClick={() => setShowImages(prev => ({ ...prev, back: !prev.back }))}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400"
              >
                {showImages.back ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {showImages.back && (
              <div className="relative bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
                {car.registration_back_url ? (
                  renderImage(car.registration_back_url, 'back', "Registration Card Back")
                ) : (
                  <div className="flex items-center justify-center h-48">
                    <p className="text-gray-500 dark:text-gray-400">No image available</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Vehicle Details Form */}
        {!isRejecting ? (
          <div className="space-y-4 mb-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Brand*
                </label>
                <input
                  type="text"
                  value={details.brand}
                  onChange={(e) => setDetails(prev => ({ ...prev, brand: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Model*
                </label>
                <input
                  type="text"
                  value={details.model}
                  onChange={(e) => setDetails(prev => ({ ...prev, model: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Year*
                </label>
                <input
                  type="number"
                  value={details.year}
                  onChange={(e) => setDetails(prev => ({ ...prev, year: e.target.value }))}
                  min="1900"
                  max={new Date().getFullYear() + 1}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Chassis Number*
                </label>
                <input
                  type="text"
                  value={details.chassisNumber}
                  onChange={(e) => setDetails(prev => ({ ...prev, chassisNumber: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  required
                />
              </div>
            </div>

            {/* Plate Number Fields */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Plate State
                </label>
                <input
                  type="text"
                  value={details.plateState}
                  onChange={(e) => setDetails(prev => ({ ...prev, plateState: e.target.value.toUpperCase() }))}
                  placeholder="DXB"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white uppercase"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Plate Code
                </label>
                <input
                  type="text"
                  value={details.plateCode}
                  onChange={(e) => setDetails(prev => ({ ...prev, plateCode: e.target.value.toUpperCase() }))}
                  placeholder="J"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white uppercase"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Plate Number
                </label>
                <input
                  type="text"
                  value={details.plateNumber}
                  onChange={(e) => setDetails(prev => ({ ...prev, plateNumber: e.target.value }))}
                  placeholder="28424"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Rejection Reason
            </label>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              placeholder="Please provide a reason for rejection"
            />
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
          {!isRejecting ? (
            <>
              <button
                onClick={() => setIsRejecting(true)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Reject
              </button>
              <button
                onClick={handleApprove}
                disabled={!details.brand || !details.model || !details.year || !details.chassisNumber}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
              >
                Approve
              </button>
            </>
          ) : (
            <button
              onClick={() => onReject(car.id, rejectionReason)}
              disabled={!rejectionReason}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
            >
              Confirm Rejection
            </button>
          )}
        </div>
      </div>
    </div>
  );
}